/**
 * Company Setup content model.
 *
 * All the content is driven by Firebase Remote Config (`company_setup` group):
 *  - `company_setup_countries`                 → CompanySetupCountry[]
 *  - `company_setup_content_{country}_{locale}` → CompanySetupContent
 *    (e.g. `company_setup_content_ae_ar`), falling back to the `_en` key and
 *    then to the bundled default in `company-setup-default-content.ts`.
 * Static UI chrome (section titles, buttons, errors) stays in Weblate.
 */

export interface CompanySetupCountry {
    /** ISO-2 code; flag and localized name are derived from it. */
    code: string;
    /** URL slug under /company-setup/{slug}. */
    slug: string;
    /** Shown as a primary button in the country pop-up and gets a page. */
    supported: boolean;
}

/** Used when `company_setup_countries` is missing or invalid. */
export const DEFAULT_COMPANY_SETUP_COUNTRIES: CompanySetupCountry[] = [
    { code: 'AE', slug: 'united-arab-emirates', supported: true }
];

export interface CompanySetupExpert {
    id: string;
    name: string;
    languagesLabel: string;
    /** ISO-2 codes of the flags shown above the card. */
    flags: string[];
    calendlyUrl: string;
}

export interface CompanySetupFaq {
    id: string;
    question: string;
    answer: string;
}

/**
 * Glossary terms shown underlined with a "?" icon; hovering shows the
 * definition. Referenced from copy via `[[termId]]` markers.
 */
export interface GlossaryTerm {
    label: string;
    definition: string;
}

/** ALL-IN-ONE section blocks. */
export interface CompanySetupService {
    id: string;
    /** Icon name from `company-setup-icons.ts`. */
    icon: string;
    title: string;
    /** Panel copy; may contain [[glossary]] markers. */
    description: string;
    image: string;
}

/** Who We Serve personas. */
export interface CompanySetupPersona {
    id: string;
    /** Icon name from `company-setup-icons.ts`. */
    icon: string;
    title: string;
    description: string;
    image: string;
}

/**
 * Questionnaire question. The first question in the list is the start;
 * each option's `next` is the id of the following question, or 'contact'.
 */
export type QuestionnaireQuestionType = 'A' | 'B';

export interface QuestionnaireOption {
    id: string;
    label: string;
    /** Type A supporting text (may contain [[glossary]] markers). */
    detail?: string;
    /** Marks answers that need manual ops follow-up (sent with the lead). */
    opsFlag?: boolean;
    next: string;
}

export interface QuestionnaireQuestion {
    id: string;
    type: QuestionnaireQuestionType;
    /** Icon name from `company-setup-icons.ts`. */
    icon: string;
    title: string;
    subtitle: string;
    options: QuestionnaireOption[];
}

export interface CompanySetupContent {
    faqs: CompanySetupFaq[];
    glossary: Record<string, GlossaryTerm>;
    services: CompanySetupService[];
    personas: CompanySetupPersona[];
    experts: CompanySetupExpert[];
    questions: QuestionnaireQuestion[];
}

export const QUESTIONNAIRE_CONTACT_STEP = 'contact';

/** Body of `POST /api/company-setup` (questionnaire submission). */
export interface CompanySetupSubmitPayload {
    /** ISO-2 code of the country the page is for. */
    countryCode: string;
    /** Answers in the order the user walked them (back-tracked ones excluded). */
    answers: { question: string; option: string }[];
    fullName: string;
    email: string;
    /** E.164. */
    phone: string;
    /** ISO-2 code. */
    nationality: string;
    locale: string;
}

/** The contact step alone, sent as soon as it's filled in. */
export type CompanySetupRegisterPayload = Omit<
    CompanySetupSubmitPayload,
    'answers'
>;

/** Contact form limits (mirrors the PRD error copy placeholders). */
export const CONTACT_FORM_LIMITS = {
    fullName: { min: 2, max: 100 },
    email: { min: 5, max: 254 }
} as const;
